<!--
  @component
  Implement the editor for a DateTime field.
  @see https://decapcms.org/docs/widgets/#Datetime
  @see https://sveltiacms.app/en/docs/fields/datetime
  @todo Replace the native `<input>` with a custom component.
-->
<script>
  import { _ } from '@sveltia/i18n';
  import { Button } from '@sveltia/ui';

  import { parseDateTimeConfig } from '$lib/services/contents/fields/date-time/config';
  import {
    getCurrentDateTime,
    getCurrentValue,
    getDate,
    getInputValue,
  } from '$lib/services/contents/fields/date-time/helpers';
  import {
    getInitialTimeZone,
    getTimeZoneLabel,
  } from '$lib/services/contents/fields/date-time/timezone';
  import { watch } from '$lib/services/utils/state.svelte';

  /**
   * @import { FieldEditorProps } from '$lib/types/private';
   * @import { DateTimeField } from '$lib/types/public';
   */

  /**
   * @typedef {object} Props
   * @property {DateTimeField} fieldConfig Field configuration.
   * @property {string | undefined} currentValue Field value.
   */

  /** @type {FieldEditorProps & Props} */
  let {
    /* eslint-disable prefer-const */
    fieldId,
    fieldConfig,
    currentValue = $bindable(),
    required = true,
    readonly = false,
    invalid = false,
    /* eslint-enable prefer-const */
  } = $props();

  let inputValue = $state('');
  let isInputFocused = $state(false);

  const { type, min, max, step, dateOnly, utc, singleCustomTimeZone } = $derived(
    parseDateTimeConfig(fieldConfig),
  );
  const timeZone = $derived(getInitialTimeZone(currentValue, fieldConfig));

  /**
   * Update {@link inputValue} based on {@link currentValue}. Only update if the input is not
   * currently focused to avoid interfering with user typing.
   */
  const setInputValue = () => {
    if (isInputFocused) {
      return;
    }

    const _inputValue = getInputValue({ currentValue, fieldConfig, timeZone });

    // Avoid a cycle dependency & infinite loop
    if (_inputValue !== undefined && _inputValue !== inputValue) {
      inputValue = _inputValue;
    }
  };

  /**
   * Update {@link currentValue} based on {@link inputValue}.
   */
  const setCurrentValue = () => {
    const _currentValue = getCurrentValue({ inputValue, currentValue, fieldConfig, timeZone });

    // Avoid a cycle dependency & infinite loop
    if (_currentValue === undefined || _currentValue === currentValue) {
      return;
    }

    const newDate = getDate(_currentValue, fieldConfig);
    const oldDate = getDate(currentValue, fieldConfig);

    if (newDate !== undefined && oldDate !== undefined) {
      // Compare the actual date/time: if a user edits an existing entry in a different location
      // than where it was originally written, `inputValue` and `_currentValue` may shift to the
      // current timezone, but the epoch won’t change. Don’t update `currentValue` in that case.
      // The dates are compared here rather than as epochs because `getDate()` returns `undefined`
      // for a value it can’t parse, and `NaN !== NaN` would report every such value as a change.
      if (newDate.getTime() === oldDate.getTime()) {
        return;
      }
    } else if (newDate === undefined && oldDate === undefined && _currentValue !== '') {
      // Neither value resolves to a date, so there’s no epoch to compare and nothing to tell the
      // two apart. Writing one unusable string over another would let this and the effect that
      // syncs `inputValue` keep waking each other. Clearing the field is the exception: an empty
      // `_currentValue` settles on the next run.
      return;
    }

    currentValue = _currentValue;
  };

  // Keep the displayed value in sync with the stored entry value.
  watch(
    () => currentValue,
    () => {
      setInputValue();
    },
  );

  // Only update currentValue when inputValue changes (not when timezone changes)
  watch(
    () => inputValue,
    () => {
      setCurrentValue();
    },
  );

  /**
   * Handle input focus event.
   */
  const handleFocus = () => {
    isInputFocused = true;
  };

  /**
   * Handle input blur event - sync values when user finishes editing.
   */
  const handleBlur = () => {
    isInputFocused = false;
    // After losing focus, ensure inputValue is synced with currentValue
    setInputValue();
  };
</script>

<div role="none">
  <input
    {...{ type, min, max, step }}
    bind:value={inputValue}
    {readonly}
    aria-readonly={readonly}
    aria-required={required}
    aria-invalid={invalid}
    aria-labelledby="{fieldId}-label"
    aria-errormessage="{fieldId}-error"
    onfocus={handleFocus}
    onblur={handleBlur}
  />
  {#if !readonly}
    <Button
      variant="tertiary"
      label={_(dateOnly ? 'today' : 'now')}
      onclick={() => {
        inputValue = getCurrentDateTime(fieldConfig, timeZone);
      }}
    />
  {/if}
  {#if !readonly && !required}
    <Button
      variant="tertiary"
      label={_('clear')}
      disabled={!currentValue}
      onclick={() => {
        currentValue = '';
      }}
    />
  {/if}
</div>

{#if singleCustomTimeZone}
  <div role="none" class="timezone">
    {getTimeZoneLabel(singleCustomTimeZone, getDate(currentValue, fieldConfig))}
  </div>
{:else if utc}
  <div role="none" class="timezone">UTC</div>
{/if}

<style>
  div {
    display: flex;
    align-items: center;
  }

  .timezone {
    margin: 4px 8px 0;
    color: var(--sui-secondary-foreground-color);
    font-size: var(--sui-font-size-small);
    white-space: nowrap;
  }
</style>
